import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { gql } from 'graphql-request'
import { gqlClient } from '@/lib/graphql'

const QUERY = gql`
  query BookOrphans($slug: String!) {
    book(slug: $slug) {
      id
      orphanedCount
      lastPullSummary
      chapters {
        id
        position
        displayTitle
        orphanedSegments {
          id
          externalId
          blockType
          sourceText
          targetText
        }
      }
    }
  }
`

const RESTORE = gql`
  mutation Restore($id: ID!) {
    restoreOrphanedSegment(input: { id: $id }) {
      segment { id status }
      errors
    }
  }
`

const DELETE = gql`
  mutation Del($id: ID!) {
    deleteOrphanedSegment(input: { id: $id }) {
      ok
      errors
    }
  }
`

export interface OrphanSegment {
  id: string
  externalId: string
  blockType: string
  sourceText: string | null
  targetText: string | null
}

export interface OrphansChapter {
  id: string
  position: number
  displayTitle: string
  orphanedSegments: OrphanSegment[]
}

export interface OrphansData {
  id: string
  orphanedCount: number
  lastPullSummary: string | null
  chapters: OrphansChapter[]
}

export function useOrphans(slug: string) {
  return useQuery({
    queryKey: ['orphans', slug],
    queryFn: async () =>
      (await gqlClient.request<{ book: OrphansData }>(QUERY, { slug })).book,
  })
}

export function useRestoreOrphan(slug: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => gqlClient.request(RESTORE, { id }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['orphans', slug] }),
  })
}

export function useDeleteOrphan(slug: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => gqlClient.request(DELETE, { id }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['orphans', slug] }),
  })
}
